import { useEffect } from "react";
import { Link } from "react-router-dom";
import { PRODUCT_MARKS, type ProductMarkKey } from "../brand/marks";
import {
  ALL_PRODUCTS,
  CERBERUS_SITE,
  SENTIENCE_SITE,
  TRACELOCK_APP,
} from "../nav/navigation";
import type { CtaLink } from "../content/types";

type Evaluation = {
  slug: ProductMarkKey;
  /** How this product is evaluated today. Must match its availability. */
  mode: string;
  headline: string;
  body: string;
  ctas: CtaLink[];
};

/**
 * Each product is offered only the evaluation it actually supports: a live
 * demo, a self-serve workspace, open source, a pilot, a design partnership or
 * a preview conversation. Nothing here may promise a trial a product lacks.
 */
export const EVALUATIONS: Evaluation[] = [
  {
    slug: "cerberus",
    mode: "Live demo",
    headline: "Watch an agent attack stopped at the tool boundary",
    body: "An agent reads privileged data, ingests an injected web page and tries to email the data out. Unprotected, it succeeds. With Cerberus in the path, the send is interrupted and nothing leaves.",
    ctas: [
      { label: "Watch the live demo", href: CERBERUS_SITE.demo, external: true },
      { label: "See pricing", href: CERBERUS_SITE.pricing, external: true },
    ],
  },
  {
    slug: "tracelock",
    mode: "Self-serve workspace",
    headline: "Run continuous compliance on your own systems",
    body: "Open a workspace, connect your stack and watch controls run on a schedule, with every result recorded as write-once evidence for your auditor.",
    ctas: [
      { label: "Start a workspace", href: TRACELOCK_APP.signUp, external: true },
      { label: "Compare editions", href: "/tracelock#editions" },
    ],
  },
  {
    slug: "argus",
    mode: "Open source",
    headline: "Point Argus Core at your own agents",
    body: "The MIT-licensed core is installable today, so your team can read the code and run it before talking to anyone. The full red-team kit is delivered as a Warden engagement.",
    ctas: [
      { label: "Get the MIT core", href: "https://github.com/Odingard/Argus", external: true },
      { label: "Request an engagement", href: "/contact?topic=argus" },
    ],
  },
  {
    slug: "sentience",
    mode: "Pilot",
    headline: "Put regulatory change on a governed workflow",
    body: "Sentience runs with a small pilot cohort. A pilot scopes the jurisdictions and obligations you track and runs them through monitoring, derivation and filing.",
    ctas: [
      { label: "Request a pilot", href: SENTIENCE_SITE.pilot },
      { label: "About Sentience", href: SENTIENCE_SITE.home },
    ],
  },
  {
    slug: "bifrost",
    mode: "Guided demo",
    headline: "See a governed exchange between two organizations",
    body: "A Bifrost demo walks through identity, delegated authority, policy decisions and signed records on synthetic data, from a security review to a federated agent action.",
    ctas: [
      { label: "Request a Bifrost demo", href: "/contact?topic=bifrost-demo" },
      { label: "About Bifrost", href: "/bifrost" },
    ],
  },
  {
    slug: "mimir",
    mode: "Preview",
    headline: "Review the approach with our researchers",
    body: "Mimir is in design with early partners. A preview session walks through how memory poisoning is detected and where it fits in your agent estate.",
    ctas: [
      { label: "Talk to us about the preview", href: "/contact?topic=mimir" },
      { label: "About Mimir", href: "/mimir" },
    ],
  },
  {
    slug: "guardian",
    mode: "Authority demo",
    headline: "Watch an AI agent hit the boundary of human authority",
    body: "The Guardian walkthrough shows an agent operating normally, escalating an exact action for approval, and then failing closed when malicious instructions try to turn access into authority.",
    ctas: [
      { label: "Request a Guardian demo", href: "/contact?topic=guardian" },
      { label: "About Guardian", href: "/guardian" },
    ],
  },
];

function Cta({ cta, primary }: { cta: CtaLink; primary: boolean }) {
  const className = primary ? "btn btn--primary" : "btn btn--quiet";
  return cta.external ? (
    <a className={className} href={cta.href}>
      {cta.label}
    </a>
  ) : (
    <Link className={className} to={cta.href}>
      {cta.label}
    </Link>
  );
}

export function DemosPage() {
  useEffect(() => {
    document.title = "Demos and Trials — Odingard";
  }, []);

  return (
    <main className="page gs" id="main">
      <section className="pillar__hero">
        <p className="page__eyebrow">Demos and Trials</p>
        <h1>See it work before you commit</h1>
        <p className="page__lede">
          Every Odingard product can be evaluated on your terms: a recorded
          live attack, a workspace you run yourself, open-source code your team
          can read, or a scoped pilot with our engineers.
        </p>
      </section>

      <section className="pdp__section" id="evaluate">
        <h2>Choose how to evaluate</h2>
        <div className="gs__grid">
          {EVALUATIONS.map((e) => {
            const product = ALL_PRODUCTS.find((p) => p.slug === e.slug);
            if (!product) return null;
            const Mark = PRODUCT_MARKS[product.mark];
            return (
              <article key={e.slug} className="gs__card">
                <p className="gs__kicker">
                  <span className="gs__lockup" style={{ color: product.accent }}>
                    <Mark size={22} />
                    <b>{product.name}</b>
                  </span>
                  <span className="gs__mode">{e.mode}</span>
                </p>
                <h3>{e.headline}</h3>
                <p className="gs__body">{e.body}</p>
                <div className="gs__ctas">
                  {e.ctas.map((cta, i) => (
                    <Cta key={cta.href} cta={cta} primary={i === 0} />
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="pdp__close">
        <h2>Prefer a guided demo</h2>
        <p className="pdp__body">
          Our engineers will walk your security and compliance leads through
          the products that fit your environment, against your use case rather
          than a script.
        </p>
        <div className="pdp__ctas">
          <Link className="btn btn--primary" to="/contact?topic=demo">
            Request a guided demo
          </Link>
          <Link className="btn btn--quiet" to="/contact?topic=briefing">
            Request an executive briefing
          </Link>
        </div>
      </section>
    </main>
  );
}
